import CircularImage from '@creator/ui/components/CircularImage/CircularImage';
import Image, { type ImageProps } from '@creator/ui/components/Image/Image';
import { mergeProps } from '@creator/ui/utils/merge-props/merge-props';
import { cn } from '@creator/ui/utils/ui';
import anonymousImg from '@src/assets/images/anonymous.png';
import notSetProfileImg from '@src/assets/images/profile.webp';
import { getDomainName } from '@src/config';
import { getLogoSrc } from '@src/model/config/helpers';
import { useStoreState } from '@src/model/hooks';
import { getProfileBadgeImage, getProfileImage } from '@src/model/user/helpers';
import { propsEqualityFn } from '@src/utils/object-utils/object-utils';
import { noop } from '@src/utils/utils';
import type { FC } from 'react';
import { memo } from 'react';

export interface ProfileImageProps {
    className?: string;
    displayName: string | 'anonymous';
    tokenName?: string;
    imageProps?: Partial<ImageProps>;
    badgeProps?: Partial<ImageProps>;
    showBadge?: boolean;
    isLoading?: boolean;
    onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

const ProfileImage: FC<ProfileImageProps> = props => {
    const { onClick = noop, displayName, className } = props;

    const myUser = useStoreState(state => state.user.myUser);
    const isAnonymous = displayName === 'anonymous';

    function getBaseClassName(): string {
        let baseClassName = 'shrink-0 relative';
        if (!isAnonymous) baseClassName += ' cursor-pointer';

        return baseClassName;
    }

    function getImageSrc(): string {
        if (getDomainName() === displayName) return getLogoSrc();
        if (isAnonymous) return anonymousImg;
        if (displayName === myUser?.displayName && myUser.profileImageUrl) return myUser.profileImageUrl;
        return getProfileImage(displayName);
    }

    function getFallbackImageSrc(): string {
        if (getDomainName() === displayName) return '';

        return notSetProfileImg;
    }

    function renderBadgeImage(className = '') {
        const { tokenName, badgeProps = {} } = props;

        if (!tokenName) return null;

        const _props = mergeProps<[ImageProps, Partial<ImageProps>]>(
            {
                src: getProfileBadgeImage(displayName, tokenName),
                className: cn(className, 'w-2/5 h-2/5 absolute bottom-0 left-0'),
            },
            badgeProps
        );

        return <Image {..._props} />;
    }

    function renderBadge() {
        const { showBadge = true, tokenName } = props;

        if (!showBadge || !tokenName) return null;
        return renderBadgeImage('absolute left-0 bottom-0 -translate-x-1/4');
    }

    function renderImage() {
        const { imageProps = {}, isLoading } = props;

        const _props = mergeProps<[ImageProps, Partial<ImageProps>]>(
            {
                src: getImageSrc(),
                className: 'h-full w-full object-cover object-center',
                fallbackSrc: getFallbackImageSrc(),
                onClick: !isAnonymous ? onClick : noop,
                alt: `${displayName} profile image`,
                isLoading,
            },
            imageProps
        );

        // attach SSR marker attribute; ImageProps doesn't declare data-* attributes
        _props['data-ssr'] = 'profile-image';

        return <CircularImage imageProps={_props} />;
    }

    function renderContent() {
        return (
            <div className={cn(getBaseClassName(), className)}>
                {renderImage()}
                {renderBadge()}
            </div>
        );
    }

    return renderContent();
};

export default memo(ProfileImage, propsEqualityFn);
